import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Coins, RefreshCw, Check, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CondividiReteDialog({ open, onOpenChange, richiesta, onSuccess }) {
  const { token } = useAuth();
  const [gettone, setGettone] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const nomeRef = richiesta
    ? `${richiesta.nome_referente} ${richiesta.cognome_referente}`.trim()
    : "";

  const handleCondividi = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/condividi`,
        {
          gettone_previsto: gettone ? parseFloat(gettone) : null,
          note_rete: note || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta condivisa in rete con successo");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella condivisione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Share2 className="w-5 h-5 text-blue-600" />
            Condividi in rete
          </DialogTitle>
          {richiesta && (
            <p className="text-sm text-slate-500 mt-1">
              Stai per condividere la richiesta di <strong>{nomeRef}</strong> nella rete Affidya.
              {richiesta.nome_ospite && ` Ospite: ${richiesta.nome_ospite}.`}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Info */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-700 space-y-1">
            <p className="font-medium">La condivisione farà sì che:</p>
            <ul className="list-disc pl-4 space-y-0.5 text-xs">
              <li>Lo stato della richiesta venga impostato a <strong>Condiviso in rete</strong></li>
              <li>La richiesta diventi visibile alle strutture partner</li>
              <li>Venga registrata un'attività in cronologia</li>
            </ul>
          </div>

          {/* Gettone */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-amber-500" />
              Gettone previsto (€) — opzionale
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
              <Input
                type="number"
                className="pl-7"
                placeholder="Es. 100"
                value={gettone}
                onChange={(e) => setGettone(e.target.value)}
                min="0"
                step="10"
              />
            </div>
            <p className="text-xs text-slate-400">
              Puoi aggiornare il gettone in qualsiasi momento dalla scheda richiesta.
            </p>
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <Label>Note per la rete</Label>
            <Textarea
              rows={2}
              placeholder="Es. Ricerca urgente zona Bari, paziente parzialmente autosufficiente..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annulla
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
            onClick={handleCondividi}
            disabled={loading}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Condividi in rete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Aggiorna gettone dalla scheda ────────────────────────────────────────────

export function AggiornaGettoneDialog({ open, onOpenChange, richiesta, onSuccess }) {
  const { token } = useAuth();
  const [statoGettone, setStatoGettone] = useState(richiesta?.stato_gettone || "In attesa");
  const [dataMaturazione, setDataMaturazione] = useState(richiesta?.data_maturazione?.slice(0, 10) || "");
  const [dataSaldo, setDataSaldo] = useState(richiesta?.data_saldo?.slice(0, 10) || "");
  const [nota, setNota] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/gettone`,
        {
          stato_gettone: statoGettone,
          data_maturazione: dataMaturazione || null,
          data_saldo: dataSaldo || null,
          nota: nota || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Gettone aggiornato: ${statoGettone}`);
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const STATO_ICONS = {
    "In attesa": <Clock className="w-4 h-4 text-slate-400" />,
    "Maturato":  <Coins className="w-4 h-4 text-amber-500" />,
    "Saldato":   <Check className="w-4 h-4 text-emerald-500" />,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-500" />
            Aggiorna gettone
          </DialogTitle>
          {richiesta?.gettone_previsto != null && (
            <p className="text-sm text-slate-500">
              Gettone previsto: <strong>{richiesta.gettone_previsto} €</strong>
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Stato gettone</Label>
            <Select value={statoGettone} onValueChange={setStatoGettone}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["In attesa", "Maturato", "Saldato"].map((s) => (
                  <SelectItem key={s} value={s}>
                    <div className="flex items-center gap-2">
                      {STATO_ICONS[s]}
                      {s}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(statoGettone === "Maturato" || statoGettone === "Saldato") && (
            <div>
              <Label>Data maturazione</Label>
              <Input type="date" className="mt-1" value={dataMaturazione} onChange={(e) => setDataMaturazione(e.target.value)} />
            </div>
          )}
          {statoGettone === "Saldato" && (
            <div>
              <Label>Data saldo</Label>
              <Input type="date" className="mt-1" value={dataSaldo} onChange={(e) => setDataSaldo(e.target.value)} />
            </div>
          )}
          <div>
            <Label>Nota</Label>
            <Input className="mt-1" placeholder="Opzionale" value={nota} onChange={(e) => setNota(e.target.value)} />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Annulla</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" onClick={handleSave} disabled={loading}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
